import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import AdminStoreList from "./AdminStoreList";
import CampaignList from "../../campanas/components/CampaignList";
import CampaignForm from "../../campanas/components/CampaignForm";
import CrearEmpresaPublica from "../../empresa/components/CrearEmpresaPublica";
import ImportarEmpresasPublicasJson from "../../empresa/components/ImportarEmpresasPublicasJson";
import QuickAccessPanel from "../../cliente/components/QuickAccessPanel";
import AdminSolicitudesProducto from "./AdminSolicitudesProducto";
import AdminSolicitudesCampana from "./AdminSolicitudesCampana";
import { formatearFecha } from "../../../utils/dateUtils";
import { isFirestoreIndexError, logFirestoreIndexError } from "../../../utils/firestoreIndexError";

function AdminPanel({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [indexErrorUserMessage, setIndexErrorUserMessage] = useState(null);
  const [mostrarCrearEmpresa, setMostrarCrearEmpresa] = useState(false);
  const [mostrarImportarJson, setMostrarImportarJson] = useState(false);
  const [stats, setStats] = useState({
    totalEmpresas: 0,
    empresasActivas: 0,
    solicitudesPendientes: 0,
    clientesPendientes: 0,
    totalCampañas: 0,
    solicitudesClientes: {
      total: 0,
      enviadas: 0,
      en_revision: 0,
      aprobadas: 0,
      rechazadas: 0,
      pendientes: 0
    },
    solicitudesEmpresas: {
      total: 0,
      pendiente: 0,
      en_revision: 0,
      activada: 0,
      credenciales_asignadas: 0,
      rechazada: 0
    },
    concesionarias: {
      total: 0,
      conAgente: 0,
      sinAgente: 0,
      activas: 0
    },
    lotes: {
      total: 0,
      activos: 0,
      perfilesComprados: 0,
      perfilesUsados: 0,
      perfilesDisponibles: 0
    },
    solicitudesLotes: {
      pendientes: 0,
      aprobadas: 0,
      rechazadas: 0,
      total: 0
    },
    contactosEmpresa: {
      total: 0,
      hoy: 0,
      estaSemana: 0,
      porEmpresa: [],
      porMarca: [],
      porServicio: [],
      visitasPorEmpresa: [] // visitas_perfil_empresa (accesos a /empresa/:id)
    },
    solicitudesProductos: { pendientes: 0 },
    solicitudesCampanas: { pendientes: 0 },
    marcasMasBuscadasHomepage: [],
    serviciosMasBuscadosHomepage: [],
    membresiasClientes: { basic: 0, premium: 0, total: 0 },
    plateChecks: { hoy: 0, ultimos7d: 0, ultimos30d: 0 }
  });
  const [solicitudesClientesRecientes, setSolicitudesClientesRecientes] = useState([]);
  const [solicitudesEmpresasRecientes, setSolicitudesEmpresasRecientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [solicitudesLotesRecientes, setSolicitudesLotesRecientes] = useState([]);
  const [comunicacionHomepage, setComunicacionHomepage] = useState({
    headline: '',
    subheadline: '',
    bullets: [],
    badges: [],
    ctaPrimary: { text: '', url: '' },
    ctaSecondary: { text: '', url: '' },
    visible: false,
    hideLanding: false
  });
  const [comunicacionLoading, setComunicacionLoading] = useState(false);
  const [comunicacionSaved, setComunicacionSaved] = useState(false);

  useEffect(() => {
    const cargarEstadisticas = async () => {
      let visitasPorEmpresaArray = [];
      try {
        console.log('📊 Cargando estadísticas del dashboard...');
        
        // Empresas
        const empresasSnapshot = await getDocs(collection(db, 'empresas'));
        const empresasActivas = empresasSnapshot.docs.filter(doc => {
          const estado = doc.data().estado;
          return estado === 'activa' || estado === 'Activa';
        });
        console.log('🏢 Empresas encontradas:', empresasSnapshot.size, 'Activas:', empresasActivas.length);
        
        // Solicitudes de empresas/proveedores pendientes (colección principal)
        const solicitudesEmpresaQuery = query(
          collection(db, 'solicitudes_empresa'),
          where('estado', 'in', ['pendiente', 'en_revision'])
        );
        const solicitudesEmpresaSnapshot = await getDocs(solicitudesEmpresaQuery);
        console.log('📋 Solicitudes empresa pendientes:', solicitudesEmpresaSnapshot.size);
        
        // Solicitudes de proveedores (colección adicional)
        const solicitudesProveedorQuery = query(
          collection(db, 'solicitudes_proveedor'),
          where('estado_general', 'in', ['enviada', 'en_revision'])
        );
        const solicitudesProveedorSnapshot = await getDocs(solicitudesProveedorQuery);
        console.log('📋 Solicitudes proveedor pendientes:', solicitudesProveedorSnapshot.size);
        
        // Solicitudes de campañas
        const solicitudesCampanaQuery = query(
          collection(db, 'solicitudes_campana'),
          where('estado', 'in', ['pendiente', 'en_revision'])
        );
        const solicitudesCampanaSnapshot = await getDocs(solicitudesCampanaQuery);
        console.log('📢 Solicitudes campaña pendientes:', solicitudesCampanaSnapshot.size);
        
        // Solicitudes de productos
        const solicitudesProductoQuery = query(
          collection(db, 'solicitudes_producto'),
          where('estado', 'in', ['pendiente', 'en_revision'])
        );
        const solicitudesProductoSnapshot = await getDocs(solicitudesProductoQuery);
        console.log('📦 Solicitudes producto pendientes:', solicitudesProductoSnapshot.size);
        
        // Solicitudes de clientes
        const solicitudesClienteQuery = query(
          collection(db, 'solicitudes_cliente'),
          orderBy('fecha_solicitud', 'desc')
        );
        let solicitudesClienteSnapshot;
        try {
          solicitudesClienteSnapshot = await getDocs(solicitudesClienteQuery);
        } catch (error) {
          console.log('Error con orderBy en solicitudes_cliente, usando sin orden:', error);
          solicitudesClienteSnapshot = await getDocs(collection(db, 'solicitudes_cliente'));
        }
        const solicitudesCliente = solicitudesClienteSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const solicitudesClienteEnviadas = solicitudesCliente.filter(s => s.estado_general === 'enviada');
        const solicitudesClienteEnRevision = solicitudesCliente.filter(s => s.estado_general === 'en_revision');
        const solicitudesClienteAprobadas = solicitudesCliente.filter(s => s.estado_general === 'aprobada');
        const solicitudesClienteRechazadas = solicitudesCliente.filter(s => s.estado_general === 'rechazada');
        const solicitudesClientePendientes = solicitudesCliente.filter(s => 
          s.estado_general === 'enviada' || s.estado_general === 'en_revision'
        );
        
        // Ordenar manualmente si no se pudo con orderBy
        solicitudesCliente.sort((a, b) => {
          const fechaA = a.fecha_solicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
          const fechaB = b.fecha_solicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
          return fechaB - fechaA;
        });
        
        setSolicitudesClientesRecientes(solicitudesClientePendientes.slice(0, 5));
        console.log('👥 Solicitudes de clientes encontradas:', solicitudesCliente.length);
        
        // Clientes pendientes
        const clientesSnapshot = await getDocs(collection(db, 'clientes'));
        console.log('👥 Clientes totales:', clientesSnapshot.size);
        
        // Solicitudes de empresas - obtener todas para estadísticas
        const solicitudesEmpresaAllSnapshot = await getDocs(collection(db, 'solicitudes_empresa'));
        const solicitudesEmpresaAll = solicitudesEmpresaAllSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const solicitudesEmpresaPendiente = solicitudesEmpresaAll.filter(s => s.estado === 'pendiente');
        const solicitudesEmpresaEnRevision = solicitudesEmpresaAll.filter(s => s.estado === 'en_revision');
        const solicitudesEmpresaActivada = solicitudesEmpresaAll.filter(s => s.estado === 'activada');
        const solicitudesEmpresaCredenciales = solicitudesEmpresaAll.filter(s => s.estado === 'credenciales_asignadas');
        const solicitudesEmpresaRechazada = solicitudesEmpresaAll.filter(s => s.estado === 'rechazada');
        
        // Ordenar solicitudes de empresas por fecha
        solicitudesEmpresaAll.sort((a, b) => {
          const fechaA = a.fecha_solicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
          const fechaB = b.fecha_solicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
          return fechaB - fechaA;
        });
        
        const solicitudesEmpresaPendientes = solicitudesEmpresaAll.filter(s => 
          s.estado === 'pendiente' || s.estado === 'en_revision'
        );
        setSolicitudesEmpresasRecientes(solicitudesEmpresaPendientes.slice(0, 5));
        console.log('🏢 Solicitudes de empresas encontradas:', solicitudesEmpresaAll.length);
        
        // Campañas
        const campañasSnapshot = await getDocs(collection(db, 'campanas'));
        console.log('📢 Campañas totales:', campañasSnapshot.size);

        // Contactos a empresas
        let contactosSnapshot;
        try {
          const contactosQuery = query(
            collection(db, 'contactos_empresa'),
            orderBy('fechaCreacion', 'desc')
          );
          contactosSnapshot = await getDocs(contactosQuery);
        } catch (e) {
          contactosSnapshot = await getDocs(collection(db, 'contactos_empresa'));
        }
        const contactos = contactosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        contactos.sort((a, b) => {
          const fa = a.fechaCreacion?.toDate?.() || new Date(0);
          const fb = b.fechaCreacion?.toDate?.() || new Date(0);
          return fb - fa;
        });
        const ahora = new Date();
        const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
        const inicioSemana = new Date(ahora);
        inicioSemana.setDate(inicioSemana.getDate() - 7);
        const contactosHoy = contactos.filter(c => {
          const f = c.fechaCreacion?.toDate?.() || new Date(0);
          return f >= inicioHoy;
        });
        const contactosSemana = contactos.filter(c => {
          const f = c.fechaCreacion?.toDate?.() || new Date(0);
          return f >= inicioSemana;
        });
        const porEmpresa = {};
        const porMarca = {};
        const porServicio = {};
        contactos.forEach(c => {
          const key = c.empresaId || c.empresaNombre || 'sin_empresa';
          if (!porEmpresa[key]) {
            porEmpresa[key] = {
              empresaId: c.empresaId,
              empresaNombre: c.empresaNombre || 'Sin nombre',
              total: 0,
              hoy: 0,
              estaSemana: 0,
              contactos: []
            };
          }
          porEmpresa[key].total++;
          porEmpresa[key].contactos.push(c);
          const f = c.fechaCreacion?.toDate?.() || new Date(0);
          if (f >= inicioHoy) porEmpresa[key].hoy++;
          if (f >= inicioSemana) porEmpresa[key].estaSemana++;
          const marca = (c.marcaVehiculo || '').trim() || 'No especificada';
          porMarca[marca] = (porMarca[marca] || 0) + 1;
          const serv = (c.tipoServicio || '').trim() || 'Sin especificar';
          porServicio[serv] = (porServicio[serv] || 0) + 1;
        });
        const porEmpresaArray = Object.values(porEmpresa).sort((a, b) => b.total - a.total);
        const porMarcaArray = Object.entries(porMarca)
          .map(([marca, count]) => ({ marca, count }))
          .filter(x => x.marca !== 'No especificada')
          .sort((a, b) => b.count - a.count);
        const porServicioArray = Object.entries(porServicio)
          .map(([servicio, count]) => ({ servicio, count }))
          .filter(x => x.servicio !== 'Sin especificar')
          .sort((a, b) => b.count - a.count);

        // Visitas al perfil (visitas_perfil_empresa)
        try {
          const visitasSnapshot = await getDocs(collection(db, 'visitas_perfil_empresa'));
          const visitasCounts = {};
          visitasSnapshot.docs.forEach((d) => {
            const eid = d.data().empresaId;
            if (eid) visitasCounts[eid] = (visitasCounts[eid] || 0) + 1;
          });
          const empresasMap = {};
          (await getDocs(collection(db, 'empresas'))).docs.forEach((d) => {
            empresasMap[d.id] = d.data().nombre || 'Sin nombre';
          });
          visitasPorEmpresaArray = Object.entries(visitasCounts)
            .map(([empresaId, total]) => ({
              empresaId,
              empresaNombre: empresasMap[empresaId] || empresaId,
              total
            }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 20);
        } catch (err) {
          console.warn('Error cargando visitas_perfil_empresa:', err);
        }

        // Búsquedas del Homepage (cuando el usuario presiona Buscar en el buscador principal)
        let marcasMasBuscadasHomepage = [];
        let serviciosMasBuscadosHomepage = [];
        try {
          const busquedasSnapshot = await getDocs(collection(db, 'busquedas_homepage'));
          const marcasCount = {};
          const serviciosCount = {};
          busquedasSnapshot.docs.forEach((docSnap) => {
            const d = docSnap.data();
            const tipo = d.tipo;
            const termino = (d.termino || '').trim();
            if (!termino || !tipo) return;
            const terminoNorm = termino.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const terminoDisplay = termino.charAt(0).toUpperCase() + termino.slice(1).toLowerCase();
            if (tipo === 'marca') {
              marcasCount[terminoNorm] = marcasCount[terminoNorm] || { termino: terminoDisplay, cantidad: 0 };
              marcasCount[terminoNorm].cantidad += 1;
            } else if (tipo === 'servicio') {
              serviciosCount[terminoNorm] = serviciosCount[terminoNorm] || { termino: terminoDisplay, cantidad: 0 };
              serviciosCount[terminoNorm].cantidad += 1;
            }
          });
          marcasMasBuscadasHomepage = Object.values(marcasCount)
            .map(({ termino, cantidad }) => ({ termino, cantidad }))
            .sort((a, b) => b.cantidad - a.cantidad)
            .slice(0, 10);
          serviciosMasBuscadosHomepage = Object.values(serviciosCount)
            .map(({ termino, cantidad }) => ({ termino, cantidad }))
            .sort((a, b) => b.cantidad - a.cantidad)
            .slice(0, 10);
        } catch (err) {
          console.warn('Error cargando busquedas_homepage:', err);
        }

        // Concesionarias
        const concesionariasQuery = query(
          collection(db, 'empresas'),
          where('tipoEmpresa', '==', 'concesionaria')
        );
        const concesionariasSnapshot = await getDocs(concesionariasQuery);
        const concesionarias = concesionariasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const concesionariasConAgente = concesionarias.filter(c => c.agenteAsignado || c.agente_id);
        const concesionariasActivas = concesionarias.filter(c => c.activo !== false);
        console.log('🚗 Concesionarias encontradas:', concesionarias.length);

        // Lotes de perfiles
        const lotesSnapshot = await getDocs(collection(db, 'lotes_perfiles'));
        const lotes = lotesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const lotesActivos = lotes.filter(l => l.activo !== false);
        const totalPerfilesComprados = lotes.reduce((sum, l) => sum + (l.cantidad || 0), 0);
        const totalPerfilesUsados = lotes.reduce((sum, l) => sum + (l.cantidadUsada || 0), 0);
        const totalPerfilesDisponibles = totalPerfilesComprados - totalPerfilesUsados;
        console.log('📦 Lotes encontrados:', lotes.length);

        // Solicitudes de lotes
        let solicitudesLotesSnapshot;
        try {
          const solicitudesLotesQuery = query(
            collection(db, 'solicitudes_lotes'),
            orderBy('fechaSolicitud', 'desc')
          );
          solicitudesLotesSnapshot = await getDocs(solicitudesLotesQuery);
        } catch (error) {
          console.log('Error con orderBy, usando sin orden:', error);
          solicitudesLotesSnapshot = await getDocs(collection(db, 'solicitudes_lotes'));
        }
        const solicitudesLotes = solicitudesLotesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const solicitudesLotesPendientes = solicitudesLotes.filter(s => s.estado === 'pendiente' || s.estado === 'en_revision');
        const solicitudesLotesAprobadas = solicitudesLotes.filter(s => s.estado === 'aprobada');
        const solicitudesLotesRechazadas = solicitudesLotes.filter(s => s.estado === 'rechazada');
        
        // Ordenar manualmente si no se pudo con orderBy
        solicitudesLotes.sort((a, b) => {
          const fechaA = a.fechaSolicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
          const fechaB = b.fechaSolicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
          return fechaB - fechaA;
        });
        
        setSolicitudesLotesRecientes(solicitudesLotesPendientes.slice(0, 5));
        console.log('📦 Solicitudes de lotes encontradas:', solicitudesLotes.length);

        // Membresías clientes (plan basic / premium) y consultas patente
        let membresiasBasic = 0, membresiasPremium = 0;
        let plateChecksHoy = 0, plateChecks7d = 0, plateChecks30d = 0;
        try {
          const usuariosClienteQuery = query(collection(db, 'usuarios'), where('rol', '==', 'cliente'));
          const usuariosClienteSnap = await getDocs(usuariosClienteQuery);
          usuariosClienteSnap.docs.forEach(d => {
            const plan = (d.data().plan || 'basic').toLowerCase();
            if (plan === 'premium') membresiasPremium++;
            else membresiasBasic++;
          });
          const plateChecksSnap = await getDocs(collection(db, 'plate_checks_logs'));
          const now = Date.now();
          const oneDay = 24 * 60 * 60 * 1000;
          plateChecksSnap.docs.forEach(d => {
            const created = d.data().createdAt?.toDate?.();
            if (!created) return;
            const ms = now - created.getTime();
            if (ms < oneDay) plateChecksHoy++;
            if (ms < 7 * oneDay) plateChecks7d++;
            if (ms < 30 * oneDay) plateChecks30d++;
          });
        } catch (e) {
          console.warn('Membresías/plate_checks:', e);
        }

        const totalSolicitudesPendientes = 
          solicitudesEmpresaSnapshot.size + 
          solicitudesProveedorSnapshot.size + 
          solicitudesCampanaSnapshot.size + 
          solicitudesProductoSnapshot.size;

        const nuevasStats = {
          totalEmpresas: empresasSnapshot.size,
          empresasActivas: empresasActivas.length,
          solicitudesPendientes: totalSolicitudesPendientes,
          clientesPendientes: clientesSnapshot.size,
          totalCampañas: campañasSnapshot.size,
          solicitudesClientes: {
            total: solicitudesCliente.length,
            enviadas: solicitudesClienteEnviadas.length,
            en_revision: solicitudesClienteEnRevision.length,
            aprobadas: solicitudesClienteAprobadas.length,
            rechazadas: solicitudesClienteRechazadas.length,
            pendientes: solicitudesClientePendientes.length
          },
          solicitudesEmpresas: {
            total: solicitudesEmpresaAll.length,
            pendiente: solicitudesEmpresaPendiente.length,
            en_revision: solicitudesEmpresaEnRevision.length,
            activada: solicitudesEmpresaActivada.length,
            credenciales_asignadas: solicitudesEmpresaCredenciales.length,
            rechazada: solicitudesEmpresaRechazada.length
          },
          concesionarias: {
            total: concesionarias.length,
            conAgente: concesionariasConAgente.length,
            sinAgente: concesionarias.length - concesionariasConAgente.length,
            activas: concesionariasActivas.length
          },
          lotes: {
            total: lotes.length,
            activos: lotesActivos.length,
            perfilesComprados: totalPerfilesComprados,
            perfilesUsados: totalPerfilesUsados,
            perfilesDisponibles: totalPerfilesDisponibles
          },
          solicitudesLotes: {
            pendientes: solicitudesLotesPendientes.length,
            aprobadas: solicitudesLotesAprobadas.length,
            rechazadas: solicitudesLotesRechazadas.length,
            total: solicitudesLotes.length
          },
          contactosEmpresa: {
            total: contactos.length,
            hoy: contactosHoy.length,
            estaSemana: contactosSemana.length,
            porEmpresa: porEmpresaArray,
            porMarca: porMarcaArray,
            porServicio: porServicioArray,
            visitasPorEmpresa: visitasPorEmpresaArray
          },
          solicitudesProductos: { pendientes: solicitudesProductoSnapshot.size },
          solicitudesCampanas: { pendientes: solicitudesCampanaSnapshot.size },
          marcasMasBuscadasHomepage,
          serviciosMasBuscadosHomepage,
          membresiasClientes: { basic: membresiasBasic, premium: membresiasPremium, total: membresiasBasic + membresiasPremium },
          plateChecks: { hoy: plateChecksHoy, ultimos7d: plateChecks7d, ultimos30d: plateChecks30d }
        };
        
        console.log('✅ Estadísticas recargadas:', nuevasStats);
        setStats(nuevasStats);
      } catch (error) {
        const isPermissionError = error?.code === 'permission-denied' || error?.message?.includes('permission');
        if (isPermissionError) {
          console.warn('⚠️ Sin permisos para cargar estadísticas del admin. Despliega firestore.rules e inicia sesión como admin.');
          setStats({
            totalEmpresas: 0,
            empresasActivas: 0,
            solicitudesPendientes: 0,
            clientesPendientes: 0,
            totalCampañas: 0,
            solicitudesClientes: { total: 0, enviadas: 0, en_revision: 0, aprobadas: 0, rechazadas: 0, pendientes: 0 },
            solicitudesEmpresas: { total: 0, pendiente: 0, en_revision: 0, activada: 0, credenciales_asignadas: 0, rechazada: 0 },
            concesionarias: { total: 0, conAgente: 0, sinAgente: 0, activas: 0 },
            lotes: { total: 0, activos: 0, perfilesComprados: 0, perfilesUsados: 0, perfilesDisponibles: 0 },
            solicitudesLotes: { pendientes: 0, aprobadas: 0, rechazadas: 0, total: 0 },
            contactosEmpresa: { total: 0, hoy: 0, estaSemana: 0, porEmpresa: [], porMarca: [], porServicio: [], visitasPorEmpresa: [] },
            solicitudesProductos: { pendientes: 0 },
            solicitudesCampanas: { pendientes: 0 },
            marcasMasBuscadasHomepage: [],
            serviciosMasBuscadosHomepage: [],
            membresiasClientes: { basic: 0, premium: 0, total: 0 },
            plateChecks: { hoy: 0, ultimos7d: 0, ultimos30d: 0 }
          });
        } else {
          console.error('❌ Error cargando estadísticas:', error);
          if (isFirestoreIndexError(error)) {
            const info = logFirestoreIndexError(error, { context: 'AdminPanel.cargarEstadisticas' });
            setIndexErrorUserMessage(info.userMessage);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    cargarEstadisticas();
  }, []);

  const recargarEstadisticas = async () => {
    setIndexErrorUserMessage(null);
    try {
      setLoading(true);
      console.log('🔄 Recargando estadísticas...');
      
      // Empresas
      const empresasSnapshot = await getDocs(collection(db, 'empresas'));
      const empresasActivas = empresasSnapshot.docs.filter(doc => {
        const estado = doc.data().estado;
        return estado === 'activa' || estado === 'Activa';
      });
      
      // Solicitudes de clientes
      let solicitudesClienteSnapshot;
      try {
        const solicitudesClienteQuery = query(
          collection(db, 'solicitudes_cliente'),
          orderBy('fecha_solicitud', 'desc')
        );
        solicitudesClienteSnapshot = await getDocs(solicitudesClienteQuery);
      } catch (error) {
        solicitudesClienteSnapshot = await getDocs(collection(db, 'solicitudes_cliente'));
      }
      const solicitudesCliente = solicitudesClienteSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const solicitudesClienteEnviadas = solicitudesCliente.filter(s => s.estado_general === 'enviada');
      const solicitudesClienteEnRevision = solicitudesCliente.filter(s => s.estado_general === 'en_revision');
      const solicitudesClienteAprobadas = solicitudesCliente.filter(s => s.estado_general === 'aprobada');
      const solicitudesClienteRechazadas = solicitudesCliente.filter(s => s.estado_general === 'rechazada');
      const solicitudesClientePendientes = solicitudesCliente.filter(s => 
        s.estado_general === 'enviada' || s.estado_general === 'en_revision'
      );
      
      solicitudesCliente.sort((a, b) => {
        const fechaA = a.fecha_solicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
        const fechaB = b.fecha_solicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
        return fechaB - fechaA;
      });
      
      setSolicitudesClientesRecientes(solicitudesClientePendientes.slice(0, 5));
      
      // Solicitudes de empresas/proveedores pendientes
      const solicitudesEmpresaQuery = query(
        collection(db, 'solicitudes_empresa'),
        where('estado', 'in', ['pendiente', 'en_revision'])
      );
      const solicitudesEmpresaSnapshot = await getDocs(solicitudesEmpresaQuery);
      
      // Solicitudes de empresas - todas para estadísticas
      const solicitudesEmpresaAllSnapshot = await getDocs(collection(db, 'solicitudes_empresa'));
      const solicitudesEmpresaAll = solicitudesEmpresaAllSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const solicitudesEmpresaPendiente = solicitudesEmpresaAll.filter(s => s.estado === 'pendiente');
      const solicitudesEmpresaEnRevision = solicitudesEmpresaAll.filter(s => s.estado === 'en_revision');
      const solicitudesEmpresaActivada = solicitudesEmpresaAll.filter(s => s.estado === 'activada');
      const solicitudesEmpresaCredenciales = solicitudesEmpresaAll.filter(s => s.estado === 'credenciales_asignadas');
      const solicitudesEmpresaRechazada = solicitudesEmpresaAll.filter(s => s.estado === 'rechazada');
      
      solicitudesEmpresaAll.sort((a, b) => {
        const fechaA = a.fecha_solicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
        const fechaB = b.fecha_solicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
        return fechaB - fechaA;
      });
      
      const solicitudesEmpresaPendientes = solicitudesEmpresaAll.filter(s => 
        s.estado === 'pendiente' || s.estado === 'en_revision'
      );
      setSolicitudesEmpresasRecientes(solicitudesEmpresaPendientes.slice(0, 5));
      
      // Solicitudes de proveedores
      const solicitudesProveedorQuery = query(
        collection(db, 'solicitudes_proveedor'),
        where('estado_general', 'in', ['enviada', 'en_revision'])
      );
      const solicitudesProveedorSnapshot = await getDocs(solicitudesProveedorQuery);
      
      // Solicitudes de campañas
      const solicitudesCampanaQuery = query(
        collection(db, 'solicitudes_campana'),
        where('estado', 'in', ['pendiente', 'en_revision'])
      );
      const solicitudesCampanaSnapshot = await getDocs(solicitudesCampanaQuery);
      
      // Solicitudes de productos
      const solicitudesProductoQuery = query(
        collection(db, 'solicitudes_producto'),
        where('estado', 'in', ['pendiente', 'en_revision'])
      );
      const solicitudesProductoSnapshot = await getDocs(solicitudesProductoQuery);
      
      // Clientes
      const clientesSnapshot = await getDocs(collection(db, 'clientes'));
      
      // Campañas
      const campañasSnapshot = await getDocs(collection(db, 'campanas'));

      // Contactos a empresas
      let contactosSnapshot;
      try {
        const contactosQuery = query(
          collection(db, 'contactos_empresa'),
          orderBy('fechaCreacion', 'desc')
        );
        contactosSnapshot = await getDocs(contactosQuery);
      } catch (e) {
        contactosSnapshot = await getDocs(collection(db, 'contactos_empresa'));
      }
      const contactos = contactosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      contactos.sort((a, b) => {
        const fa = a.fechaCreacion?.toDate?.() || new Date(0);
        const fb = b.fechaCreacion?.toDate?.() || new Date(0);
        return fb - fa;
      });
      const ahora = new Date();
      const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
      const inicioSemana = new Date(ahora);
      inicioSemana.setDate(inicioSemana.getDate() - 7);
      const contactosHoy = contactos.filter(c => {
        const f = c.fechaCreacion?.toDate?.() || new Date(0);
        return f >= inicioHoy;
      });
      const contactosSemana = contactos.filter(c => {
        const f = c.fechaCreacion?.toDate?.() || new Date(0);
        return f >= inicioSemana;
      });
      const porEmpresa = {};
      const porMarca = {};
      const porServicio = {};
      contactos.forEach(c => {
        const key = c.empresaId || c.empresaNombre || 'sin_empresa';
        if (!porEmpresa[key]) {
          porEmpresa[key] = {
            empresaId: c.empresaId,
            empresaNombre: c.empresaNombre || 'Sin nombre',
            total: 0,
            hoy: 0,
            estaSemana: 0,
            contactos: []
          };
        }
        porEmpresa[key].total++;
        porEmpresa[key].contactos.push(c);
        const f = c.fechaCreacion?.toDate?.() || new Date(0);
        if (f >= inicioHoy) porEmpresa[key].hoy++;
        if (f >= inicioSemana) porEmpresa[key].estaSemana++;
        const marca = (c.marcaVehiculo || '').trim() || 'No especificada';
        porMarca[marca] = (porMarca[marca] || 0) + 1;
        const serv = (c.tipoServicio || '').trim() || 'Sin especificar';
        porServicio[serv] = (porServicio[serv] || 0) + 1;
      });
      const porEmpresaArray = Object.values(porEmpresa).sort((a, b) => b.total - a.total);

      // Visitas al perfil (visitas_perfil_empresa)
      let visitasPorEmpresaArray = [];
      try {
        const visitasSnapshot = await getDocs(collection(db, 'visitas_perfil_empresa'));
        const visitasCounts = {};
        visitasSnapshot.docs.forEach((d) => {
          const eid = d.data().empresaId;
          if (eid) visitasCounts[eid] = (visitasCounts[eid] || 0) + 1;
        });
        const empresasMap = {};
        (await getDocs(collection(db, 'empresas'))).docs.forEach((d) => {
          empresasMap[d.id] = d.data().nombre || 'Sin nombre';
        });
        visitasPorEmpresaArray = Object.entries(visitasCounts)
          .map(([empresaId, total]) => ({
            empresaId,
            empresaNombre: empresasMap[empresaId] || empresaId,
            total
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 20);
      } catch (err) {
        console.warn('Error cargando visitas_perfil_empresa:', err);
      }

      const porMarcaArray = Object.entries(porMarca)
        .map(([marca, count]) => ({ marca, count }))
        .filter(x => x.marca !== 'No especificada')
        .sort((a, b) => b.count - a.count);
      const porServicioArray = Object.entries(porServicio)
        .map(([servicio, count]) => ({ servicio, count }))
        .filter(x => x.servicio !== 'Sin especificar')
        .sort((a, b) => b.count - a.count);

      // Búsquedas del Homepage (cuando el usuario presiona Buscar)
      let marcasMasBuscadasHomepage = [];
      let serviciosMasBuscadosHomepage = [];
      try {
        const busquedasSnapshot = await getDocs(collection(db, 'busquedas_homepage'));
        const marcasCount = {};
        const serviciosCount = {};
        busquedasSnapshot.docs.forEach((docSnap) => {
          const d = docSnap.data();
          const tipo = d.tipo;
          const termino = (d.termino || '').trim();
          if (!termino || !tipo) return;
          const terminoNorm = termino.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const terminoDisplay = termino.charAt(0).toUpperCase() + termino.slice(1).toLowerCase();
          if (tipo === 'marca') {
            marcasCount[terminoNorm] = marcasCount[terminoNorm] || { termino: terminoDisplay, cantidad: 0 };
            marcasCount[terminoNorm].cantidad += 1;
          } else if (tipo === 'servicio') {
            serviciosCount[terminoNorm] = serviciosCount[terminoNorm] || { termino: terminoDisplay, cantidad: 0 };
            serviciosCount[terminoNorm].cantidad += 1;
          }
        });
        marcasMasBuscadasHomepage = Object.values(marcasCount)
          .map(({ termino, cantidad }) => ({ termino, cantidad }))
          .sort((a, b) => b.cantidad - a.cantidad)
          .slice(0, 10);
        serviciosMasBuscadosHomepage = Object.values(serviciosCount)
          .map(({ termino, cantidad }) => ({ termino, cantidad }))
          .sort((a, b) => b.cantidad - a.cantidad)
          .slice(0, 10);
      } catch (err) {
        console.warn('Error cargando busquedas_homepage:', err);
      }

      // Concesionarias
      const concesionariasQuery = query(
        collection(db, 'empresas'),
        where('tipoEmpresa', '==', 'concesionaria')
      );
      const concesionariasSnapshot = await getDocs(concesionariasQuery);
      const concesionarias = concesionariasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const concesionariasConAgente = concesionarias.filter(c => c.agenteAsignado || c.agente_id);
      const concesionariasActivas = concesionarias.filter(c => c.activo !== false);

      // Lotes de perfiles
      const lotesSnapshot = await getDocs(collection(db, 'lotes_perfiles'));
      const lotes = lotesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const lotesActivos = lotes.filter(l => l.activo !== false);
      const totalPerfilesComprados = lotes.reduce((sum, l) => sum + (l.cantidad || 0), 0);
      const totalPerfilesUsados = lotes.reduce((sum, l) => sum + (l.cantidadUsada || 0), 0);
      const totalPerfilesDisponibles = totalPerfilesComprados - totalPerfilesUsados;

      // Solicitudes de lotes
      let solicitudesLotesSnapshot;
      try {
        const solicitudesLotesQuery = query(
          collection(db, 'solicitudes_lotes'),
          orderBy('fechaSolicitud', 'desc')
        );
        solicitudesLotesSnapshot = await getDocs(solicitudesLotesQuery);
      } catch (error) {
        solicitudesLotesSnapshot = await getDocs(collection(db, 'solicitudes_lotes'));
      }
      const solicitudesLotes = solicitudesLotesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const solicitudesLotesPendientes = solicitudesLotes.filter(s => s.estado === 'pendiente' || s.estado === 'en_revision');
      const solicitudesLotesAprobadas = solicitudesLotes.filter(s => s.estado === 'aprobada');
      const solicitudesLotesRechazadas = solicitudesLotes.filter(s => s.estado === 'rechazada');
      
      solicitudesLotes.sort((a, b) => {
        const fechaA = a.fechaSolicitud?.toDate?.() || (a.fechaCreacion ? new Date(a.fechaCreacion) : new Date(0));
        const fechaB = b.fechaSolicitud?.toDate?.() || (b.fechaCreacion ? new Date(b.fechaCreacion) : new Date(0));
        return fechaB - fechaA;
      });
      
      setSolicitudesLotesRecientes(solicitudesLotesPendientes.slice(0, 5));

      let membresiasBasic = 0, membresiasPremium = 0;
      let plateChecksHoy = 0, plateChecks7d = 0, plateChecks30d = 0;
      try {
        const usuariosClienteQuery = query(collection(db, 'usuarios'), where('rol', '==', 'cliente'));
        const usuariosClienteSnap = await getDocs(usuariosClienteQuery);
        usuariosClienteSnap.docs.forEach(d => {
          const plan = (d.data().plan || 'basic').toLowerCase();
          if (plan === 'premium') membresiasPremium++;
          else membresiasBasic++;
        });
        const plateChecksSnap = await getDocs(collection(db, 'plate_checks_logs'));
        const now = Date.now();
        const oneDay = 24 * 60 * 60 * 1000;
        plateChecksSnap.docs.forEach(d => {
          const created = d.data().createdAt?.toDate?.();
          if (!created) return;
          const ms = now - created.getTime();
          if (ms < oneDay) plateChecksHoy++;
          if (ms < 7 * oneDay) plateChecks7d++;
          if (ms < 30 * oneDay) plateChecks30d++;
        });
      } catch (e) {
        console.warn('Membresías/plate_checks:', e);
      }

      const totalSolicitudesPendientes = 
        solicitudesEmpresaSnapshot.size + 
        solicitudesProveedorSnapshot.size + 
        solicitudesCampanaSnapshot.size + 
        solicitudesProductoSnapshot.size;

      const nuevasStats = {
        totalEmpresas: empresasSnapshot.size,
        empresasActivas: empresasActivas.length,
        solicitudesPendientes: totalSolicitudesPendientes,
        clientesPendientes: clientesSnapshot.size,
        totalCampañas: campañasSnapshot.size,
        solicitudesClientes: {
          total: solicitudesCliente.length,
          enviadas: solicitudesClienteEnviadas.length,
          en_revision: solicitudesClienteEnRevision.length,
          aprobadas: solicitudesClienteAprobadas.length,
          rechazadas: solicitudesClienteRechazadas.length,
          pendientes: solicitudesClientePendientes.length
        },
        solicitudesEmpresas: {
          total: solicitudesEmpresaAll.length,
          pendiente: solicitudesEmpresaPendiente.length,
          en_revision: solicitudesEmpresaEnRevision.length,
          activada: solicitudesEmpresaActivada.length,
          credenciales_asignadas: solicitudesEmpresaCredenciales.length,
          rechazada: solicitudesEmpresaRechazada.length
        },
        concesionarias: {
          total: concesionarias.length,
          conAgente: concesionariasConAgente.length,
          sinAgente: concesionarias.length - concesionariasConAgente.length,
          activas: concesionariasActivas.length
        },
        lotes: {
          total: lotes.length,
          activos: lotesActivos.length,
          perfilesComprados: totalPerfilesComprados,
          perfilesUsados: totalPerfilesUsados,
          perfilesDisponibles: totalPerfilesDisponibles
        },
        solicitudesLotes: {
          pendientes: solicitudesLotesPendientes.length,
          aprobadas: solicitudesLotesAprobadas.length,
          rechazadas: solicitudesLotesRechazadas.length,
          total: solicitudesLotes.length
        },
        contactosEmpresa: {
          total: contactos.length,
          hoy: contactosHoy.length,
          estaSemana: contactosSemana.length,
          porEmpresa: porEmpresaArray,
          porMarca: porMarcaArray,
          porServicio: porServicioArray,
          visitasPorEmpresa: visitasPorEmpresaArray
        },
        solicitudesProductos: { pendientes: solicitudesProductoSnapshot.size },
        solicitudesCampanas: { pendientes: solicitudesCampanaSnapshot.size },
        marcasMasBuscadasHomepage,
        serviciosMasBuscadosHomepage,
        membresiasClientes: { basic: membresiasBasic, premium: membresiasPremium, total: membresiasBasic + membresiasPremium },
        plateChecks: { hoy: plateChecksHoy, ultimos7d: plateChecks7d, ultimos30d: plateChecks30d }
      };
      
      console.log('✅ Estadísticas recargadas:', nuevasStats);
      setStats(nuevasStats);
    } catch (error) {
      console.error('❌ Error recargando estadísticas:', error);
      if (isFirestoreIndexError(error)) {
        const info = logFirestoreIndexError(error, { context: 'AdminPanel.recargarEstadisticas' });
        setIndexErrorUserMessage(info.userMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {indexErrorUserMessage && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-center justify-between">
          <p className="text-amber-800 text-sm">{indexErrorUserMessage}</p>
          <button type="button" onClick={() => setIndexErrorUserMessage(null)} className="text-amber-600 hover:text-amber-800 font-medium text-sm">Cerrar</button>
        </div>
      )}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Panel de Administración
          </h1>
          <p className="text-gray-600">
            {user?.email}. Gestiona todo el sistema desde aquí.
          </p>
        </div>
        <button
          onClick={recargarEstadisticas}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Cargando...
            </>
          ) : (
            <>
              🔄 Actualizar Estadísticas
            </>
          )}
        </button>
      </div>

      {/* Pestañas de navegación */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "overview"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
          onClick={() => setTab("overview")}
        >
          📊 Resumen
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "empresas"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
          onClick={() => setTab("empresas")}
        >
          🏢 Empresas
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "campañas"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
          onClick={() => setTab("campañas")}
        >
          📢 Solicitudes Campañas
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "productos"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
          onClick={() => setTab("productos")}
        >
          📦 Productos
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "membresias"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
          onClick={() => setTab("membresias")}
        >
          👥 Membresías Clientes
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === "comunicacion"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
          onClick={() => setTab("comunicacion")}
        >
          📢 Comunicación Homepage
        </button>
      </div>

      {/* Contenido de pestañas */}
      {tab === "overview" && (
        <div className="space-y-6">
          {/* Empresas con visitas al perfil y búsquedas del Homepage */}
          <div className="bg-white rounded-lg shadow-lg border-2 border-teal-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <span>📞</span>
                Empresas con visitas al perfil
              </h2>
              <Link
                to="/admin/contactos-empresa"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Ver reporte completo →
              </Link>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Visitas al perfil (accesos a /empresa/:id) y contactos recibidos (formulario de WhatsApp).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-800">
                  {loading ? '...' : stats.contactosEmpresa?.total || 0}
                </div>
                <div className="text-green-600 text-sm font-medium">Total contactos</div>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-800">
                  {loading ? '...' : stats.contactosEmpresa?.hoy || 0}
                </div>
                <div className="text-blue-600 text-sm font-medium">Hoy</div>
              </div>
              <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4">
                <div className="text-2xl font-bold text-indigo-800">
                  {loading ? '...' : stats.contactosEmpresa?.estaSemana || 0}
                </div>
                <div className="text-indigo-600 text-sm font-medium">Últimos 7 días</div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">🏢 Empresas con más visitas al perfil</h3>
                <p className="text-xs text-gray-500 mb-2">Accesos a /empresa/:id (clientes registrados o no)</p>
                {stats.contactosEmpresa?.visitasPorEmpresa?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.contactosEmpresa.visitasPorEmpresa.slice(0, 8).map((emp, idx) => (
                      <div key={emp.empresaId || idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <Link
                          to={emp.empresaId ? `/empresa/${emp.empresaId}` : '#'}
                          target={emp.empresaId ? '_blank' : undefined}
                          className="text-blue-600 hover:underline font-medium text-sm truncate flex-1 mr-2"
                          title={emp.empresaNombre}
                        >
                          {emp.empresaNombre}
                        </Link>
                        <span className="text-gray-700 font-semibold shrink-0">{emp.total}</span>
                      </div>
                    ))}
                    {stats.contactosEmpresa.visitasPorEmpresa.length > 8 && (
                      <Link to="/admin/empresas" className="text-blue-600 hover:underline text-xs">
                        +{stats.contactosEmpresa.visitasPorEmpresa.length - 8} más
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">Sin visitas registradas</div>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">🚗 Marcas (al contactar)</h3>
                {stats.contactosEmpresa?.porMarca?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.contactosEmpresa.porMarca.slice(0, 8).map((m, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <span className="text-gray-800 text-sm truncate flex-1">{m.marca}</span>
                        <span className="text-gray-600 font-semibold shrink-0">{m.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">Sin datos de marcas</div>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">🔧 Servicios más buscados</h3>
                {stats.contactosEmpresa?.porServicio?.length > 0 ? (
                  <div className="space-y-2">
                    {stats.contactosEmpresa.porServicio.slice(0, 8).map((s, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <span className="text-gray-800 text-sm truncate flex-1" title={s.servicio}>{s.servicio}</span>
                        <span className="text-gray-600 font-semibold shrink-0">{s.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">Sin datos de servicios</div>
                )}
              </div>
            </div>
            {(!stats.contactosEmpresa?.porEmpresa?.length && !stats.contactosEmpresa?.porMarca?.length && !stats.contactosEmpresa?.porServicio?.length) && (
              <div className="text-center py-6 text-gray-500 text-sm">
                No hay contactos registrados
              </div>
            )}

            {/* Búsquedas en Homepage (marcas y servicios del buscador principal) */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-base font-semibold text-gray-800 mb-3">🔍 Marcas y servicios más buscados (Homepage)</h3>
              <p className="text-xs text-gray-500 mb-4">Medido cuando el usuario presiona el botón Buscar en el buscador principal del HomePage.</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">🚗 Marcas más buscadas</h4>
                  {stats.marcasMasBuscadasHomepage?.length > 0 ? (
                    <div className="space-y-2">
                      {stats.marcasMasBuscadasHomepage.slice(0, 10).map((m, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                          <span className="text-gray-800 text-sm truncate flex-1">{m.termino}</span>
                          <span className="text-gray-600 font-semibold shrink-0">{m.cantidad}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">Sin búsquedas de marcas</div>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">🔧 Servicios más buscados</h4>
                  {stats.serviciosMasBuscadosHomepage?.length > 0 ? (
                    <div className="space-y-2">
                      {stats.serviciosMasBuscadosHomepage.slice(0, 10).map((s, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                          <span className="text-gray-800 text-sm truncate flex-1" title={s.termino}>{s.termino}</span>
                          <span className="text-gray-600 font-semibold shrink-0">{s.cantidad}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">Sin búsquedas de servicios</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Estadísticas principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setTab("empresas")}>
              <div className="text-2xl font-bold text-blue-800">
                {loading ? '...' : stats.totalEmpresas}
              </div>
              <div className="text-blue-600 text-sm">Total Empresas</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="text-2xl font-bold text-green-800">
                {loading ? '...' : stats.empresasActivas}
              </div>
              <div className="text-green-600 text-sm">Empresas Activas</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/admin/clientes-kawescar?tab=solicitudes')}>
              <div className="text-2xl font-bold text-yellow-800">
                {loading ? '...' : stats.solicitudesClientes?.pendientes || 0}
              </div>
              <div className="text-yellow-600 text-sm">Solicitudes Clientes</div>
              <div className="text-xs text-yellow-500 mt-1">
                {loading ? '...' : `${stats.solicitudesClientes?.total || 0} total`}
              </div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/admin/solicitudes-registro')}>
              <div className="text-2xl font-bold text-orange-800">
                {loading ? '...' : (stats.solicitudesEmpresas?.pendiente || 0) + (stats.solicitudesEmpresas?.en_revision || 0)}
              </div>
              <div className="text-orange-600 text-sm">Solicitudes Empresas</div>
              <div className="text-xs text-orange-500 mt-1">
                {loading ? '...' : `${stats.solicitudesEmpresas?.total || 0} total`}
              </div>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/admin/contactos-empresa')}>
              <div className="text-2xl font-bold text-teal-800">
                {loading ? '...' : stats.contactosEmpresa?.total || 0}
              </div>
              <div className="text-teal-600 text-sm">Visitas al perfil</div>
              <div className="text-xs text-teal-500 mt-1">
                {loading ? '...' : `${stats.contactosEmpresa?.hoy || 0} hoy`}
              </div>
            </div>
          </div>
          {/* Solicitudes Productos y Campañas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/admin/solicitudes-productos')}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-green-800">
                    {loading ? '...' : stats.solicitudesProductos?.pendientes || 0}
                  </div>
                  <div className="text-green-600 text-sm font-medium">📦 Solicitudes Productos</div>
                  <div className="text-xs text-green-500 mt-1">Pendientes de validación</div>
                </div>
                <span className="text-3xl">📦</span>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/admin/solicitudes-campanas')}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-amber-800">
                    {loading ? '...' : stats.solicitudesCampanas?.pendientes || 0}
                  </div>
                  <div className="text-amber-600 text-sm font-medium">📢 Solicitudes Campañas</div>
                  <div className="text-xs text-amber-500 mt-1">Pendientes de validación</div>
                </div>
                <span className="text-3xl">📢</span>
              </div>
            </div>
          </div>
          
          {/* Sección de Solicitudes de Clientes y Empresas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Solicitudes de Clientes */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span>👥</span>
                  <span>Solicitudes de Clientes</span>
                  {stats.solicitudesClientes?.pendientes > 0 && (
                    <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {stats.solicitudesClientes.pendientes}
                    </span>
                  )}
                </h2>
                <Link
                  to="/admin/clientes-kawescar?tab=solicitudes"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Ver todas →
                </Link>
              </div>
              
              {/* Estadísticas rápidas */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-3">
                  <div className="text-2xl font-bold text-yellow-800">
                    {loading ? '...' : stats.solicitudesClientes?.pendientes || 0}
                  </div>
                  <div className="text-yellow-600 text-xs font-medium">Pendientes</div>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-800">
                    {loading ? '...' : stats.solicitudesClientes?.aprobadas || 0}
                  </div>
                  <div className="text-green-600 text-xs font-medium">Aprobadas</div>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-3">
                  <div className="text-2xl font-bold text-blue-800">
                    {loading ? '...' : stats.solicitudesClientes?.en_revision || 0}
                  </div>
                  <div className="text-blue-600 text-xs font-medium">En Revisión</div>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-800">
                    {loading ? '...' : stats.solicitudesClientes?.rechazadas || 0}
                  </div>
                  <div className="text-red-600 text-xs font-medium">Rechazadas</div>
                </div>
              </div>
              
              {/* Solicitudes recientes */}
              {solicitudesClientesRecientes.length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Recientes:</h3>
                  {solicitudesClientesRecientes.map((solicitud) => (
                    <div
                      key={solicitud.id}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition-all cursor-pointer"
                      onClick={() => navigate('/admin/clientes-kawescar?tab=solicitudes')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">
                            {solicitud.nombres} {solicitud.apellidos}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {solicitud.email}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {solicitud.fecha_solicitud?.toDate 
                              ? formatearFecha(solicitud.fecha_solicitud)
                              : solicitud.fechaCreacion 
                              ? new Date(solicitud.fechaCreacion).toLocaleDateString('es-CL')
                              : 'Fecha no disponible'}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          solicitud.estado_general === 'aprobada' ? 'bg-green-200 text-green-800' :
                          solicitud.estado_general === 'rechazada' ? 'bg-red-200 text-red-800' :
                          solicitud.estado_general === 'en_revision' ? 'bg-blue-200 text-blue-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {solicitud.estado_general || 'enviada'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No hay solicitudes pendientes de clientes
                </div>
              )}
              
              <Link
                to="/admin/clientes-kawescar?tab=solicitudes"
                className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-center block font-semibold"
              >
                Ver Todas las Solicitudes de Clientes
              </Link>
            </div>
            
            {/* Solicitudes de Empresas */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span>🏢</span>
                  <span>Solicitudes de Empresas</span>
                  {(stats.solicitudesEmpresas?.pendiente || 0) + (stats.solicitudesEmpresas?.en_revision || 0) > 0 && (
                    <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {(stats.solicitudesEmpresas?.pendiente || 0) + (stats.solicitudesEmpresas?.en_revision || 0)}
                    </span>
                  )}
                </h2>
                <Link
                  to="/admin/solicitudes-registro"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Ver todas →
                </Link>
              </div>
              
              {/* Estadísticas rápidas */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-800">
                    {loading ? '...' : (stats.solicitudesEmpresas?.pendiente || 0) + (stats.solicitudesEmpresas?.en_revision || 0)}
                  </div>
                  <div className="text-orange-600 text-xs font-medium">Pendientes</div>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-800">
                    {loading ? '...' : stats.solicitudesEmpresas?.activada || 0}
                  </div>
                  <div className="text-green-600 text-xs font-medium">Activadas</div>
                </div>
                <div className="bg-purple-50 border-l-4 border-purple-500 rounded-lg p-3">
                  <div className="text-2xl font-bold text-purple-800">
                    {loading ? '...' : stats.solicitudesEmpresas?.credenciales_asignadas || 0}
                  </div>
                  <div className="text-purple-600 text-xs font-medium">Con Credenciales</div>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3">
                  <div className="text-2xl font-bold text-red-800">
                    {loading ? '...' : stats.solicitudesEmpresas?.rechazada || 0}
                  </div>
                  <div className="text-red-600 text-xs font-medium">Rechazadas</div>
                </div>
              </div>
              
              {/* Solicitudes recientes */}
              {solicitudesEmpresasRecientes.length > 0 ? (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Recientes:</h3>
                  {solicitudesEmpresasRecientes.map((solicitud) => (
                    <div
                      key={solicitud.id}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition-all cursor-pointer"
                      onClick={() => navigate('/admin/solicitudes-registro')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">
                            {solicitud.nombreEmpresa || solicitud.razonSocial || 'Sin nombre'}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {solicitud.email || solicitud.correo || 'Sin email'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {solicitud.fecha_solicitud?.toDate 
                              ? formatearFecha(solicitud.fecha_solicitud)
                              : solicitud.fechaCreacion 
                              ? new Date(solicitud.fechaCreacion).toLocaleDateString('es-CL')
                              : 'Fecha no disponible'}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          solicitud.estado === 'activada' || solicitud.estado === 'credenciales_asignadas' ? 'bg-green-200 text-green-800' :
                          solicitud.estado === 'rechazada' ? 'bg-red-200 text-red-800' :
                          solicitud.estado === 'en_revision' ? 'bg-blue-200 text-blue-800' :
                          'bg-yellow-200 text-yellow-800'
                        }`}>
                          {solicitud.estado || 'pendiente'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No hay solicitudes pendientes de empresas
                </div>
              )}
              
              <Link
                to="/admin/solicitudes-registro"
                className="mt-4 w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-center block font-semibold"
              >
                Ver Todas las Solicitudes de Empresas
              </Link>
            </div>
          </div>

          {/* Estadísticas de Concesionarias y Lotes - DESHABILITADO TEMPORALMENTE */}
          {false && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300 rounded-xl p-6 mb-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <span>🚗</span>
                <span>Gestión de Concesionarias y Lotes</span>
              </h2>
              <Link
                to="/admin/gestion-concesionarias"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-all shadow-md flex items-center gap-2"
              >
                <span>Gestionar</span>
                <span>→</span>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border-l-4 border-purple-500 rounded-lg p-4 shadow-md hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate('/admin/gestion-concesionarias')}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-purple-800">
                      {loading ? '...' : stats.concesionarias?.total || 0}
                    </div>
                    <div className="text-purple-600 text-sm font-medium">Concesionarias</div>
                    <div className="text-xs text-purple-500 mt-1">
                      {loading ? '...' : `${stats.concesionarias?.activas || 0} activas`}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {loading ? '...' : `${stats.concesionarias?.conAgente || 0} con agente`}
                    </div>
                  </div>
                  <div className="text-4xl">🚗</div>
                </div>
              </div>
              
              <div className="bg-white border-l-4 border-orange-500 rounded-lg p-4 shadow-md hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate('/admin/gestion-concesionarias')}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-orange-800">
                      {loading ? '...' : stats.lotes?.total || 0}
                    </div>
                    <div className="text-orange-600 text-sm font-medium">Lotes Totales</div>
                    <div className="text-xs text-orange-500 mt-1">
                      {loading ? '...' : `${stats.lotes?.activos || 0} activos`}
                    </div>
                  </div>
                  <div className="text-4xl">📦</div>
                </div>
              </div>
              
              <div className="bg-white border-l-4 border-green-500 rounded-lg p-4 shadow-md hover:shadow-lg transition-all cursor-pointer" onClick={() => navigate('/admin/gestion-concesionarias')}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-green-800">
                      {loading ? '...' : stats.lotes?.perfilesDisponibles || 0}
                    </div>
                    <div className="text-green-600 text-sm font-medium">Perfiles Disponibles</div>
                    <div className="text-xs text-green-500 mt-1">
                      {loading ? '...' : `${stats.lotes?.perfilesUsados || 0} usados`}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {loading ? '...' : `${stats.lotes?.perfilesComprados || 0} comprados`}
                    </div>
                  </div>
                  <div className="text-4xl">✅</div>
                </div>
              </div>
              
              <div className="bg-white border-l-4 border-red-500 rounded-lg p-4 shadow-md hover:shadow-lg transition-all cursor-pointer relative" onClick={() => navigate('/admin/gestion-concesionarias?tab=solicitudes-lotes')}>
                {stats.solicitudesLotes?.pendientes > 0 && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {stats.solicitudesLotes.pendientes}
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-red-800">
                      {loading ? '...' : stats.solicitudesLotes?.pendientes || 0}
                    </div>
                    <div className="text-red-600 text-sm font-medium">Solicitudes Pendientes</div>
                    <div className="text-xs text-red-500 mt-1">
                      {loading ? '...' : `${stats.solicitudesLotes?.aprobadas || 0} aprobadas`}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {loading ? '...' : `${stats.solicitudesLotes?.total || 0} total`}
                    </div>
                  </div>
                  <div className="text-4xl">⏳</div>
                </div>
              </div>
            </div>
            
            {/* Botones de Acceso Rápido */}
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to="/admin/gestion-concesionarias"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-all shadow-md flex items-center gap-2"
              >
                <span>🚗</span>
                <span>Gestionar Concesionarias</span>
              </Link>
              <Link
                to="/admin/gestion-concesionarias?tab=solicitudes-lotes"
                className={`px-4 py-2 rounded-lg font-semibold transition-all shadow-md flex items-center gap-2 ${
                  stats.solicitudesLotes?.pendientes > 0
                    ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                <span>📦</span>
                <span>Atender Solicitudes de Lotes</span>
                {stats.solicitudesLotes?.pendientes > 0 && (
                  <span className="bg-white text-red-600 text-xs font-bold px-2 py-0.5 rounded-full">
                    {stats.solicitudesLotes.pendientes}
                  </span>
                )}
              </Link>
              <Link
                to="/admin/gestion-concesionarias?tab=solicitudes"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-md flex items-center gap-2"
              >
                <span>👤</span>
                <span>Solicitudes de Agentes</span>
              </Link>
            </div>
          </div>
          )}

          {/* Solicitudes de Lotes Pendientes - DESHABILITADO TEMPORALMENTE */}
          {false && solicitudesLotesRecientes.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <span>📦</span>
                    <span>Solicitudes de Lotes Pendientes</span>
                    {stats.solicitudesLotes.pendientes > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {stats.solicitudesLotes.pendientes}
                      </span>
                    )}
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">
                    {stats.solicitudesLotes.pendientes} solicitud(es) requieren atención
                  </p>
                </div>
                <Link
                  to="/admin/gestion-concesionarias"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-md flex items-center gap-2"
                >
                  <span>Ver Todas</span>
                  <span>→</span>
                </Link>
              </div>
              <div className="space-y-3">
                {solicitudesLotesRecientes.map((solicitud) => (
                  <div
                    key={solicitud.id}
                    className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 hover:bg-yellow-100 transition-all cursor-pointer"
                    onClick={() => navigate('/admin/gestion-concesionarias?tab=solicitudes-lotes')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">
                            {solicitud.concesionariaNombre || 'Concesionaria'}
                          </span>
                          <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">
                            {solicitud.estado === 'pendiente' ? '⏳ Pendiente' : '🔍 En Revisión'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">{solicitud.nombreLote}</span> - {solicitud.cantidad} perfiles - ${solicitud.precio?.toLocaleString('es-CL') || '0'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {solicitud.fechaSolicitud?.toDate 
                            ? formatearFecha(solicitud.fechaSolicitud)
                            : solicitud.fechaCreacion 
                            ? new Date(solicitud.fechaCreacion).toLocaleDateString('es-CL')
                            : 'Fecha no disponible'}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/admin/gestion-concesionarias?tab=solicitudes-lotes');
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-all ml-4"
                      >
                        Atender
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Panel de Acceso Rápido */}
          <QuickAccessPanel />

          {/* Accesos rápidos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">🚀 Acciones Rápidas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/admin/empresas"
                className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg"
              >
                <div className="text-2xl mb-2">🏢</div>
                <div className="font-medium">Gestionar Empresas</div>
                <div className="text-sm opacity-90">Ver y editar perfiles</div>
              </Link>
              
              <Link
                to="/admin/solicitudes-registro"
                className="bg-yellow-600 hover:bg-yellow-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg relative"
              >
                {(stats.solicitudesEmpresas?.pendiente || 0) + (stats.solicitudesEmpresas?.en_revision || 0) > 0 && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {(stats.solicitudesEmpresas?.pendiente || 0) + (stats.solicitudesEmpresas?.en_revision || 0)}
                  </span>
                )}
                <div className="text-2xl mb-2">📋</div>
                <div className="font-medium">Solicitudes Empresas</div>
                <div className="text-sm opacity-90">
                  {(stats.solicitudesEmpresas?.pendiente || 0) + (stats.solicitudesEmpresas?.en_revision || 0) > 0
                    ? `${(stats.solicitudesEmpresas?.pendiente || 0) + (stats.solicitudesEmpresas?.en_revision || 0)} pendiente(s)`
                    : 'Revisar pendientes'}
                </div>
              </Link>
              
              <Link
                to="/admin/clientes-kawescar?tab=solicitudes"
                className="bg-indigo-600 hover:bg-indigo-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg relative"
              >
                {stats.solicitudesClientes?.pendientes > 0 && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {stats.solicitudesClientes.pendientes}
                  </span>
                )}
                <div className="text-2xl mb-2">👥</div>
                <div className="font-medium">Solicitudes Clientes</div>
                <div className="text-sm opacity-90">
                  {stats.solicitudesClientes?.pendientes > 0
                    ? `${stats.solicitudesClientes.pendientes} pendiente(s)`
                    : 'Revisar solicitudes'}
                </div>
              </Link>
              
              {/* CONCESIONARIAS Y LOTES - DESHABILITADO TEMPORALMENTE
              <Link
                to="/admin/gestion-concesionarias"
                className="bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg relative"
              >
                {stats.solicitudesLotes.pendientes > 0 && (
                  <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                    {stats.solicitudesLotes.pendientes}
                  </span>
                )}
                <div className="text-2xl mb-2">🚗</div>
                <div className="font-medium">Gestionar Concesionarias</div>
                <div className="text-sm opacity-90">Lotes y solicitudes</div>
              </Link>
              
              <Link
                to="/admin/gestion-concesionarias?tab=solicitudes-lotes"
                className="bg-red-600 hover:bg-red-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg relative"
              >
                {stats.solicitudesLotes.pendientes > 0 && (
                  <span className="absolute top-2 right-2 bg-yellow-400 text-red-900 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {stats.solicitudesLotes.pendientes}
                  </span>
                )}
                <div className="text-2xl mb-2">📦</div>
                <div className="font-medium">Atender Solicitudes Lotes</div>
                <div className="text-sm opacity-90">
                  {stats.solicitudesLotes.pendientes > 0 
                    ? `${stats.solicitudesLotes.pendientes} pendiente(s)`
                    : 'Sin pendientes'}
                </div>
              </Link>
              */}
              
              <button
                onClick={() => setTab("campañas")}
                className="bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg"
              >
                <div className="text-2xl mb-2">📢</div>
                <div className="font-medium">Solicitudes Campañas</div>
                <div className="text-sm opacity-90">Aprobar campañas</div>
              </button>
              
              <button
                onClick={() => setTab("productos")}
                className="bg-orange-600 hover:bg-orange-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg"
              >
                <div className="text-2xl mb-2">📦</div>
                <div className="font-medium">Solicitudes Productos</div>
                <div className="text-sm opacity-90">Aprobar productos</div>
              </button>
              
              <Link
                to="/admin/contactos-empresa"
                className="bg-teal-600 hover:bg-teal-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg"
              >
                <div className="text-2xl mb-2">📞</div>
                <div className="font-medium">Contactos a Empresas</div>
                <div className="text-sm opacity-90">Reporte detallado</div>
              </Link>

              <Link
                to="/admin/clientes-kawescar"
                className="bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg"
              >
                <div className="text-2xl mb-2">👥</div>
                <div className="font-medium">Clientes KawésCar</div>
                <div className="text-sm opacity-90">Ver clientes, asignar Premium</div>
              </Link>

              <Link
                to="/admin/reportes-datos-vehiculo"
                className="bg-amber-600 hover:bg-amber-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg"
              >
                <div className="text-2xl mb-2">🚗</div>
                <div className="font-medium">Reportes datos vehículo</div>
                <div className="text-sm opacity-90">Datos por patente no coinciden</div>
              </Link>

              <Link
                to="/admin/estadisticas"
                className="bg-indigo-600 hover:bg-indigo-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg"
              >
                <div className="text-2xl mb-2">📊</div>
                <div className="font-medium">Estadísticas</div>
                <div className="text-sm opacity-90">Ver métricas</div>
              </Link>

              <Link
                to="/admin/agentes-campo"
                className="bg-teal-600 hover:bg-teal-700 text-white p-4 rounded-lg text-center transition-colors shadow-md hover:shadow-lg"
              >
                <div className="text-2xl mb-2">👤</div>
                <div className="font-medium">Gestionar Agentes</div>
                <div className="text-sm opacity-90">Agentes de campo</div>
              </Link>
            </div>
          </div>

          {/* Información de Concesionarias y Lotes - DESHABILITADO TEMPORALMENTE */}
          {false && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Resumen de Concesionarias */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span>🚗</span>
                  <span>Resumen de Concesionarias</span>
                </h2>
                <Link
                  to="/admin/gestion-concesionarias"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Ver todas →
                </Link>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">🚗</div>
                    <div>
                      <div className="font-semibold text-gray-900">Total Concesionarias</div>
                      <div className="text-xs text-gray-600">{stats.concesionarias.activas} activas</div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">
                    {loading ? '...' : stats.concesionarias.total}
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">👤</div>
                    <div>
                      <div className="font-semibold text-gray-900">Con Agente Asignado</div>
                      <div className="text-xs text-gray-600">Agentes activos</div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {loading ? '...' : stats.concesionarias.conAgente}
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">⚠️</div>
                    <div>
                      <div className="font-semibold text-gray-900">Sin Agente</div>
                      <div className="text-xs text-gray-600">Requieren asignación</div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {loading ? '...' : stats.concesionarias.sinAgente}
                  </div>
                </div>
              </div>
            </div>

            {/* Resumen de Lotes */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <span>📦</span>
                  <span>Resumen de Lotes</span>
                </h2>
                <Link
                  to="/admin/gestion-concesionarias"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Ver detalles →
                </Link>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">📦</div>
                    <div>
                      <div className="font-semibold text-gray-900">Lotes Totales</div>
                      <div className="text-xs text-gray-600">{stats.lotes.activos} activos</div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-orange-600">
                    {loading ? '...' : stats.lotes.total}
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">✅</div>
                    <div>
                      <div className="font-semibold text-gray-900">Perfiles Disponibles</div>
                      <div className="text-xs text-gray-600">Listos para usar</div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {loading ? '...' : stats.lotes.perfilesDisponibles}
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">📊</div>
                    <div>
                      <div className="font-semibold text-gray-900">Perfiles Usados</div>
                      <div className="text-xs text-gray-600">
                        {loading ? '...' : `${stats.lotes.perfilesComprados} comprados`}
                      </div>
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {loading ? '...' : stats.lotes.perfilesUsados}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {tab === "empresas" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Gestión de Empresas</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMostrarImportarJson(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                📥 Importar Empresas (JSON)
              </button>
              <button
                onClick={() => setMostrarCrearEmpresa(!mostrarCrearEmpresa)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {mostrarCrearEmpresa ? "Ocultar Formulario" : "Crear Nueva Empresa"}
              </button>
            </div>
          </div>
          
          {mostrarImportarJson && (
            <ImportarEmpresasPublicasJson
              onClose={() => setMostrarImportarJson(false)}
              onSuccess={() => {
                setMostrarImportarJson(false);
                recargarEstadisticas();
                setTab('overview');
              }}
            />
          )}
          
          {mostrarCrearEmpresa && (
            <CrearEmpresaPublica 
              onClose={() => setMostrarCrearEmpresa(false)}
              onSuccess={() => {
                setMostrarCrearEmpresa(false);
                // Forzar recarga de estadísticas y cambiar a tab overview
                recargarEstadisticas();
                setTab('overview');
              }}
            />
          )}
          
          <AdminStoreList />
        </div>
      )}

      {tab === "campañas" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Gestión de Solicitudes de Campañas</h2>
            <div className="text-sm text-gray-600">
              Revisa y aprueba las campañas que los proveedores solicitan publicar
            </div>
          </div>
          
          <AdminSolicitudesCampana />
        </div>
      )}

      {tab === "productos" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Gestión de Solicitudes de Productos</h2>
            <div className="text-sm text-gray-600">
              Revisa y aprueba los productos que los proveedores solicitan publicar
            </div>
          </div>
          
          <AdminSolicitudesProducto />
        </div>
      )}

      {tab === "membresias" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Membresías Clientes / Uso</h2>
            <button
              onClick={recargarEstadisticas}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Actualizar
            </button>
          </div>
          <p className="text-gray-600 text-sm">
            Planes de cliente (Básica 2 vehículos, 2 consultas/mes; Premium 10 y 10). Consultas desde <code className="bg-gray-100 px-1 rounded">plate_checks_logs</code>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <div className="text-2xl font-bold text-gray-900">{loading ? '...' : stats.membresiasClientes?.total ?? 0}</div>
              <div className="text-sm text-gray-500">Clientes con plan</div>
            </div>
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <div className="text-2xl font-bold text-blue-800">{loading ? '...' : stats.membresiasClientes?.basic ?? 0}</div>
              <div className="text-sm text-blue-600">Básica</div>
            </div>
            <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
              <div className="text-2xl font-bold text-purple-800">{loading ? '...' : stats.membresiasClientes?.premium ?? 0}</div>
              <div className="text-sm text-purple-600">Premium</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <div className="text-sm font-semibold text-gray-700 mb-2">Consultas patente</div>
              <div className="space-y-1 text-sm">
                <div>Hoy: <strong>{loading ? '...' : stats.plateChecks?.hoy ?? 0}</strong></div>
                <div>Últimos 7 días: <strong>{loading ? '...' : stats.plateChecks?.ultimos7d ?? 0}</strong></div>
                <div>Últimos 30 días: <strong>{loading ? '...' : stats.plateChecks?.ultimos30d ?? 0}</strong></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "comunicacion" && (
        <ComunicacionHomepageForm
          data={comunicacionHomepage}
          setData={setComunicacionHomepage}
          loading={comunicacionLoading}
          setLoading={setComunicacionLoading}
          saved={comunicacionSaved}
          setSaved={setComunicacionSaved}
        />
      )}
    </div>
  );
}

function ComunicacionHomepageForm({ data, setData, loading, setLoading, saved, setSaved }) {
  const ref = doc(db, 'config', 'homepage');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      setLoading(true);
      getDoc(ref).then((snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setData({
            headline: d.headline ?? d.titulo ?? '',
            subheadline: d.subheadline ?? d.mensaje ?? '',
            bullets: Array.isArray(d.bullets) ? d.bullets : (d.bullets ? [d.bullets] : []),
            badges: Array.isArray(d.badges) ? d.badges : (d.badges ? [d.badges] : []),
            ctaPrimary: d.ctaPrimary && typeof d.ctaPrimary === 'object' ? { text: d.ctaPrimary.text ?? '', url: d.ctaPrimary.url ?? '' } : { text: '', url: '' },
            ctaSecondary: d.ctaSecondary && typeof d.ctaSecondary === 'object' ? { text: d.ctaSecondary.text ?? '', url: d.ctaSecondary.url ?? '' } : { text: '', url: '' },
            visible: d.visible === true,
            hideLanding: d.hideLanding === true
          });
        }
        setLoaded(true);
        setLoading(false);
      }).catch(() => { setLoaded(true); setLoading(false); });
    }
  }, [loaded]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaved(false);
    try {
      const bulletsArr = typeof data.bullets === 'string' ? data.bullets.split('\n').map(s => s.trim()).filter(Boolean) : (data.bullets || []);
      const badgesArr = typeof data.badges === 'string' ? data.badges.split('\n').map(s => s.trim()).filter(Boolean) : (data.badges || []);
      await setDoc(ref, {
        headline: data.headline || '',
        subheadline: data.subheadline || '',
        bullets: bulletsArr,
        badges: badgesArr,
        ctaPrimary: { text: data.ctaPrimary?.text || '', url: data.ctaPrimary?.url || '' },
        ctaSecondary: { text: data.ctaSecondary?.text || '', url: data.ctaSecondary?.url || '' },
        visible: data.visible,
        hideLanding: data.hideLanding === true,
        actualizado: serverTimestamp()
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.warn('Error guardando comunicación homepage:', err);
    }
    setLoading(false);
  };

  const bulletsStr = Array.isArray(data.bullets) ? data.bullets.join('\n') : (data.bullets || '');
  const badgesStr = Array.isArray(data.badges) ? data.badges.join('\n') : (data.badges || '');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Landing (mini hero) Homepage</h2>
        {saved && <span className="text-green-600 text-sm font-medium">Guardado. Los cambios se ven en la homepage.</span>}
      </div>
      <p className="text-gray-600 text-sm">
        Badges, título, subtítulo, bullets y dos CTAs. Estructura: headline, subheadline, bullets[], badges[], ctaPrimary, ctaSecondary. Activa &quot;Visible&quot; para publicar.
      </p>
      <form onSubmit={handleSave} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm max-w-2xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Headline (título)</label>
          <input
            type="text"
            value={data.headline}
            onChange={(e) => setData({ ...data, headline: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            placeholder="Ej: Bienvenidos a la comunidad KawésCar"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subheadline (subtítulo)</label>
          <textarea
            value={data.subheadline}
            onChange={(e) => setData({ ...data, subheadline: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20"
            placeholder="Una línea o párrafo corto debajo del título."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Badges (uno por línea)</label>
          <textarea
            value={badgesStr}
            onChange={(e) => setData({ ...data, badges: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20"
            placeholder="Nuevo\nGratis"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bullets (uno por línea)</label>
          <textarea
            value={bulletsStr}
            onChange={(e) => setData({ ...data, bullets: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 h-28"
            placeholder="Busca repuestos por marca\nRecibe cotizaciones directas\nRegístrate gratis"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CTA principal (texto)</label>
            <input
              type="text"
              value={data.ctaPrimary?.text || ''}
              onChange={(e) => setData({ ...data, ctaPrimary: { ...data.ctaPrimary, text: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Crear perfil gratis"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CTA principal (URL)</label>
            <input
              type="text"
              value={data.ctaPrimary?.url || ''}
              onChange={(e) => setData({ ...data, ctaPrimary: { ...data.ctaPrimary, url: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="/registro-cliente"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CTA secundario (texto)</label>
            <input
              type="text"
              value={data.ctaSecondary?.text || ''}
              onChange={(e) => setData({ ...data, ctaSecondary: { ...data.ctaSecondary, text: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Ver empresas"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CTA secundario (URL)</label>
            <input
              type="text"
              value={data.ctaSecondary?.url || ''}
              onChange={(e) => setData({ ...data, ctaSecondary: { ...data.ctaSecondary, url: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="/proveedores"
            />
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="comunicacion-visible"
              checked={data.visible}
              onChange={(e) => setData({ ...data, visible: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="comunicacion-visible" className="text-sm font-medium text-gray-700">Visible en homepage (mostrar contenido personalizado)</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="comunicacion-hide-landing"
              checked={data.hideLanding === true}
              onChange={(e) => setData({ ...data, hideLanding: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="comunicacion-hide-landing" className="text-sm font-medium text-gray-700">Ocultar landing completamente (no mostrar la sección en la homepage)</label>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {loading ? 'Guardando…' : 'Guardar y publicar en homepage'}
        </button>
      </form>
    </div>
  );
}

export default AdminPanel;